
import { platformApiFetch } from '@/services/platformApi/client';
import { ContactFormData } from "@/types/contact";
import { debugLogger } from '@/utils/debug';

/**
 * Submit a contact form
 * @param data Contact form data
 * @returns True if submission was successful
 */
export async function submitContactForm(data: ContactFormData): Promise<boolean> {
  try {
    const payload = {
      name: data.name,
      email: data.email,
      category: data.category,
      message: data.message,
      review_id: data.review_id || null,
      report_reason: data.report_reason || null,
      page_url: data.page_url || null
    };

    debugLogger.log("Submitting contact form data:", {
      category: data.category,
      hasReviewId: !!data.review_id,
      hasUserId: !!data.user_id,
    });

    await platformApiFetch('/v1/contact-form/submit', {
      method: 'POST',
      body: JSON.stringify({
        ...payload,
        phone_number: data.phone_number || null,
        provider_id: data.provider_id || null,
        provider_name: data.provider_name || null,
      }),
    });

    if (data.category === 'Report a Review' && data.review_id) {
      try {
        await platformApiFetch(`/v1/reviews/${data.review_id}/report`, {
          method: 'POST',
          body: JSON.stringify({
            reason: data.report_reason || 'No reason provided',
          }),
        });
      } catch (reviewError) {
        console.error("Error marking review as reported:", reviewError);
      }
    }

    return true;
  } catch (error) {
    console.error("Exception in submitContactForm:", error);
    return false;
  }
}

/**
 * Generate HTML email content
 */
const generateEmailHtml = (data: ContactFormData): string => {
  const providerInfo = data.provider_name 
    ? `<tr>
        <td style="padding: 12px 12px 12px 0;">Provider</td>
        <td style="padding: 12px 0;">${data.provider_name}${data.provider_id ? ` (ID: ${data.provider_id})` : ''}</td>
      </tr>` 
    : '';
  
  const pageInfo = data.page_url 
    ? `<tr>
        <td style="padding: 12px 12px 12px 0;">Page URL</td>
        <td style="padding: 12px 0;"><a href="${data.page_url}" style="color: #1EAEDB;">${data.page_url}</a></td>
      </tr>` 
    : '';

  return `<!DOCTYPE html>
<html>
  <head>
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
    <title>${data.category} - Contact Form</title>
    <style>
      @media only screen and (max-width: 620px) {
        table.body h1 {
          font-size: 28px !important;
          margin-bottom: 10px !important;
        }
        table.body p,
        table.body ul,
        table.body ol,
        table.body td,
        table.body span,
        table.body a {
          font-size: 16px !important;
        }
        table.body .wrapper,
        table.body .article {
          padding: 10px !important;
        }
        table.body .content {
          padding: 0 !important;
        }
        table.body .container {
          padding: 0 !important;
          width: 100% !important;
        }
        table.body .main {
          border-left-width: 0 !important;
          border-radius: 0 !important;
          border-right-width: 0 !important;
        }
      }
    </style>
  </head>
  <body style="background-color: #f8f9fa; font-family: sans-serif; -webkit-font-smoothing: antialiased; font-size: 14px; line-height: 1.4; margin: 0; padding: 0; -ms-text-size-adjust: 100%; -webkit-text-size-adjust: 100%;">
    <table role="presentation" border="0" cellpadding="0" cellspacing="0" class="body" style="border-collapse: separate; width: 100%; background-color: #f8f9fa;">
      <tr>
        <td style="font-family: sans-serif; font-size: 14px; vertical-align: top;">&nbsp;</td>
        <td class="container" style="font-family: sans-serif; font-size: 14px; vertical-align: top; display: block; max-width: 580px; padding: 10px; width: 580px; margin: 0 auto !important;">
          <div class="content" style="box-sizing: border-box; display: block; margin: 0 auto; max-width: 580px; padding: 10px;">
            <!-- START CENTERED WHITE CONTAINER -->
            <table role="presentation" class="main" style="border-collapse: separate; width: 100%; background: #ffffff; border-radius: 8px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);">
              <!-- START MAIN CONTENT AREA -->
              <tr>
                <td class="wrapper" style="font-family: sans-serif; font-size: 14px; vertical-align: top; box-sizing: border-box; padding: 20px;">
                  <div style="background-color: #4ade80; height: 5px; width: 100%; margin-bottom: 20px; border-radius: 4px;"></div>
                  <h1 style="color: #333333; font-family: sans-serif; font-weight: 700; margin: 0; margin-bottom: 20px; font-size: 24px;">${data.category}</h1>
                  <table role="presentation" border="0" cellpadding="0" cellspacing="0" style="border-collapse: separate; width: 100%;">
                    <tr>
                      <td style="padding: 12px 12px 12px 0;">From</td>
                      <td style="padding: 12px 0; font-weight: 600;">${data.name} (${data.email}${data.phone_number ? `, ${data.phone_number}` : ''})</td>
                    </tr>
                    ${providerInfo}
                    ${pageInfo}
                    <tr>
                      <td colspan="2" style="padding: 24px 0 12px 0; font-weight: 600;">Message:</td>
                    </tr>
                    <tr>
                      <td colspan="2" style="padding: 12px 16px; background-color: #f8f9fa; border-radius: 8px; border-left: 4px solid #4ade80;">
                        <p style="font-family: sans-serif; font-size: 14px; line-height: 1.6; margin: 0;">${data.message.replace(/\n/g, '<br>')}</p>
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
              <!-- END MAIN CONTENT AREA -->
            </table>
            <!-- END CENTERED WHITE CONTAINER -->
            <!-- START FOOTER -->
            <div class="footer" style="clear: both; margin-top: 10px; text-align: center; width: 100%;">
              <table role="presentation" border="0" cellpadding="0" cellspacing="0" style="border-collapse: separate; width: 100%;">
                <tr>
                  <td class="content-block" style="font-family: sans-serif; vertical-align: top; padding-bottom: 10px; padding-top: 10px; color: #8e9196; font-size: 12px; text-align: center;">
                    <span>This message was sent from the contact form on your website.</span>
                  </td>
                </tr>
              </table>
            </div>
            <!-- END FOOTER -->
          </div>
        </td>
        <td style="font-family: sans-serif; font-size: 14px; vertical-align: top;">&nbsp;</td>
      </tr>
    </table>
  </body>
</html>`;
};

/**
 * Mark a contact message as resolved
 * @param messageId The message ID to mark as resolved
 * @param resolutionNotes Optional notes about the resolution
 * @returns True if successful
 */
export async function markMessageResolved(messageId: string, resolutionNotes?: string): Promise<boolean> {
  try {
    await platformApiFetch(`/v1/contact-messages/resolve/${messageId}`, {
      method: 'POST',
      body: JSON.stringify({ resolutionNotes: resolutionNotes || null }),
    });
    
    return true;
  } catch (error) {
    console.error("Exception in markMessageResolved:", error);
    return false;
  }
}
